import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../utils/axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import BulkUploadLogsDrawer from './BulkUploadLogsDrawer';

const BulkUploadActionsMenu = ({ row }) => {
  const dispatch = useDispatch();
  const { snackbarMessages, bulkUpload } = useTranslatedMessages();
  const bulkUploadMessages = snackbarMessages.bulk_upload ?? {};
  const actionsLabels = bulkUpload?.actions_menu ?? {};
  const [anchorEl, setAnchorEl] = useState(null);
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFile = async (downloadType) => {
    try {
      dispatch(showSnackbar({ message: bulkUploadMessages.download_in_progress, severity: 'info' }));

      const response = await apiClient.get(`/bulk-uploads/${row.id}/download`, {
        params: { downloadType },
        responseType: 'blob', // important for file downloads
        headers: {
          'Content-Type': undefined, // override default JSON
        },
      });

      const fileData = response.data;

      if (!(fileData instanceof Blob) || fileData.size === 0) {
        throw new Error('Empty file received');
      }

      const headers = response.headers || {};
      const xFilename =
        typeof headers['x-suggested-filename'] === 'string'
          ? headers['x-suggested-filename']
          : Array.isArray(headers['x-suggested-filename'])
            ? headers['x-suggested-filename'][0]
            : undefined;

      const finalFileName = xFilename || `${downloadType}_report.xlsx`;

      const downloadUrl = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', finalFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      dispatch(showSnackbar({ message: bulkUploadMessages.download_success, severity: 'success' }));
    } catch (error) {
      console.error('Download error:', error);
      dispatch(
        showSnackbar({
          message: bulkUploadMessages.download_error,
          severity: 'error',
        }),
      );
    } finally {
      handleClose();
    }
  };

  const actionsDisabled = row.status === 'UPLOADED' || row.status === 'PROCESSING' || row.status === 'UPLOAD_FAILED';

  return (
    <>
      <IconButton onClick={handleClick} disabled={actionsDisabled}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              boxShadow: '4px 4px 10px 0px rgba(0,0,0,0.2)',
            },
          },
        }}
      >
        {row.errorDocumentsCount > 0 && (
          <MenuItem onClick={() => handleDownloadFile('error')}>
            {actionsLabels.error_report ?? 'Error Report'}
          </MenuItem>
        )}
        {row.processedDocumentsCount > 0 && (
          <MenuItem onClick={() => handleDownloadFile('success')}>
            {actionsLabels.success_report ?? 'Success Report'}
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDownloadFile('original')}>
          {actionsLabels.original_file ?? 'Original File'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setLogsDrawerOpen(true);
          }}
        >
          {actionsLabels.logs ?? 'Logs'}
        </MenuItem>
      </Menu>
      <BulkUploadLogsDrawer open={logsDrawerOpen} onClose={() => setLogsDrawerOpen(false)} bulkUploadId={row.id} />
    </>
  );
};

export default BulkUploadActionsMenu;
